/**
 * Estado de adesão ao boleto único, por UC (tarefa #5576).
 *
 * A pergunta que a tela e o painel (#5579) precisam responder é uma só: **esta UC está
 * cobrando Vigor + Equatorial junto, e com que respaldo?** São quatro respostas, não
 * duas — e a quarta é a que interessa hoje.
 *
 * Aceite e liberação são coisas diferentes. `boleto_unico` é o que a emissão lê; o
 * aceite é a autorização do cliente. As 77 UCs ligadas à mão antes desta tarefa existir
 * estão liberadas SEM aceite — o estado `liberado_sem_aceite` existe para não escondê-las
 * atrás de um rótulo verde.
 */

import type { BoletoUnicoAceiteCanal } from '../../../types';

export type StatusAceiteBoletoUnico =
  /** Nem aceitou nem está no boleto único. É o estado da maior parte da base. */
  | 'sem_aceite'
  /** Aceitou, mas ainda não foi liberada — falta a checagem de débito automático. */
  | 'aceito_aguardando'
  /** Aceitou e está no boleto único. É o caminho completo. */
  | 'liberado'
  /** Está no boleto único sem aceite registrado: ligada à mão antes da #5576. */
  | 'liberado_sem_aceite';

/** Só o que a derivação lê da UC — os espelhos mantidos por trigger. */
export interface UcAceiteBoletoUnico {
  boleto_unico?: boolean | null;
  boleto_unico_aceite_em?: string | null;
  boleto_unico_aceite_canal?: string | null;
}

export function derivarStatusAceite(uc: UcAceiteBoletoUnico): StatusAceiteBoletoUnico {
  const temAceite = Boolean(uc.boleto_unico_aceite_em);
  const liberado = uc.boleto_unico === true;
  if (liberado) return temAceite ? 'liberado' : 'liberado_sem_aceite';
  return temAceite ? 'aceito_aguardando' : 'sem_aceite';
}

export interface RotuloStatus {
  texto: string;
  /** Como a tela deve tratar visualmente: neutro, em espera, ok, ou pendência real. */
  tom: 'neutro' | 'espera' | 'ok' | 'alerta';
  /** Frase de apoio — o que a pessoa faz a seguir. */
  ajuda: string;
}

export const ROTULOS_STATUS: Record<StatusAceiteBoletoUnico, RotuloStatus> = {
  sem_aceite: {
    texto: 'Sem aceite',
    tom: 'neutro',
    ajuda: 'O cliente ainda não aderiu. Continua recebendo boleto da Vigor e fatura da Equatorial separados.',
  },
  aceito_aguardando: {
    texto: 'Aceito, aguardando liberação',
    tom: 'espera',
    ajuda: 'O cliente aceitou. Falta liberar — o boleto único só passa a valer depois disso.',
  },
  liberado: {
    texto: 'Liberado',
    tom: 'ok',
    ajuda: 'Cobrança unificada ativa, com aceite registrado.',
  },
  liberado_sem_aceite: {
    texto: 'Liberado sem aceite',
    tom: 'alerta',
    ajuda: 'Está no boleto único, mas não há registro de que o cliente aceitou. Registre o aceite ou desligue.',
  },
};

export const ROTULOS_CANAL: Record<BoletoUnicoAceiteCanal, string> = {
  portal: 'Portal do Consumidor',
  whatsapp: 'WhatsApp',
  manual: 'Registrado pela Vigor',
  termo: 'Assinatura do termo',
};

export function rotuloCanal(canal?: string | null): string {
  return ROTULOS_CANAL[(canal || '') as BoletoUnicoAceiteCanal] || '—';
}

/**
 * A cláusula vale "para faturas cujo mês referência seja OUT/2026 em diante". Um aceite
 * assinado em setembro é válido, mas só produz efeito a partir dessa data — quem consome
 * isso é a emissão (#5577). Aqui só respondemos se a vigência já começou.
 */
export const VIGENCIA_PADRAO = '2026-10-01';

/**
 * O dia corrente em Brasília, `AAAA-MM-DD`.
 *
 * Ancorado em `America/Sao_Paulo` de propósito, e não em `getFullYear()/getMonth()/
 * getDate()`, que devolvem o dia no fuso DO PROCESSO. Edge function e serverless rodam em
 * UTC: às 21h de Brasília já é o dia seguinte em UTC, e a vigência começaria 3 horas
 * antes da virada real — decidindo cobrança unificada de um cliente cedo demais.
 * `en-CA` porque é o locale que formata como ISO.
 */
function diaEmBrasilia(momento: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(momento);
}

export function vigenciaJaComecou(vigenciaAPartirDe?: string | null, hoje = new Date()): boolean {
  const inicio = (vigenciaAPartirDe || VIGENCIA_PADRAO).slice(0, 10);
  return diaEmBrasilia(hoje) >= inicio;
}

/**
 * O aceite existe mas ainda não vale? É o caso de quem aceitar em setembro. A tela
 * precisa dizer isso, senão a pessoa liga a UC e estranha o boleto não mudar.
 */
export function aceitePendenteDeVigencia(
  uc: UcAceiteBoletoUnico,
  vigenciaAPartirDe?: string | null,
  hoje = new Date()
): boolean {
  return Boolean(uc.boleto_unico_aceite_em) && !vigenciaJaComecou(vigenciaAPartirDe, hoje);
}
